import React from 'react';
import { Link } from 'react-router-dom';
import { useAppSelector } from '../stores/AppStore';
import {
  selectIsMentor,
  selectSubscriptionStatus,
} from '../features/user/userSlice';

interface SubscriptionRequiredProperties {
  feature: string;
  children?: React.ReactNode;
  className?: string;
  customThinkerMessage?: string;
}

export const SubscriptionRequired: React.FC<SubscriptionRequiredProperties> = ({
  feature,
  children,
  className = '',
  customThinkerMessage,
}) => {
  const isMentor = useAppSelector(selectIsMentor);
  const subscriptionStatus = useAppSelector(selectSubscriptionStatus);

  const getSubscriptionMessage = () => {
    if (subscriptionStatus?.cancelAtPeriodEnd) {
      return {
        title: 'Subscription Ending Soon',
        message: `Your subscription will end on ${
          subscriptionStatus.currentPeriodEnd
            ? new Date(subscriptionStatus.currentPeriodEnd).toLocaleDateString(
                'en-US',
              )
            : 'the end of the current period'
        }. To continue accessing ${feature}, please renew your subscription.`,
        actionText: 'Renew Subscription',
      };
    }

    return {
      title: 'Subscription Required',
      message: `Your subscription has expired. To access ${feature}, please renew your subscription.`,
      actionText: 'Renew Subscription',
    };
  };

  const { title, message, actionText } = getSubscriptionMessage();

  return (
    <div
      className={`bg-blue-50 border border-blue-200 rounded-lg p-6 ${className}`}
    >
      <div className="flex flex-col items-center text-center">
        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
          <svg
            className="w-8 h-8 text-blue-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
            />
          </svg>
        </div>

        <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>

        <p className="text-gray-600 mb-6 max-w-md">{message}</p>

        <div className="flex flex-col sm:flex-row gap-3">
          {isMentor && (
            <Link
              to="/manage-subscription"
              className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
            >
              {actionText}
            </Link>
          )}

          {!isMentor && (
            <p className="text-sm text-gray-500">
              {customThinkerMessage ||
                'Please contact your mentor to renew the subscription.'}
            </p>
          )}
        </div>

        <div className="mt-6 pt-4 border-t border-blue-200">
          <p className="text-sm text-gray-600">
            You can still view your past results and progress. All your data is
            safely stored.
          </p>
        </div>

        {children && <div className="mt-4 w-full">{children}</div>}
      </div>
    </div>
  );
};

// Hook to check if subscription validation should be applied
export const useSubscriptionValidation = () => {
  const subscriptionStatus = useAppSelector(selectSubscriptionStatus);
  const isMentorOrThinker = useAppSelector(
    (state) =>
      state.user.user?.roles?.includes('mentor' as any) ||
      state.user.user?.roles?.includes('thinker' as any),
  );

  // Default to true (allow access) if no billing data exists - they might be legacy/school users
  const hasActiveSubscription =
    subscriptionStatus?.hasActiveSubscription ?? true;

  // Only enforce subscription check if they previously had a subscription
  const hadSubscription =
    subscriptionStatus?.wasSubscribed === true ||
    subscriptionStatus?.cancelAtPeriodEnd !== undefined;

  const shouldCheckSubscription = isMentorOrThinker && hadSubscription;

  return {
    hasActiveSubscription,
    shouldCheckSubscription,
    subscriptionStatus,
    canAccess: !shouldCheckSubscription || hasActiveSubscription,
  };
};

export default SubscriptionRequired;
